// Preference ratings — a normalised [0,1] score expressing how much the user
// likes a recipe or ingredient, used to weight menu generation.
//
// A rating is a float in [0,1] snapped to a 7-point Likert scale
// (Strongly dislike … Strongly like). It is a per-user LABEL:
//   • recipes store `rating` directly on the (per-user) recipe row;
//   • ingredients are a SHARED catalogue, so their per-user rating lives in a
//     private `ingredient-pref#<id>` overlay row (see model/ingredient-prefs.js).
//
// Generation semantics:
//   • an explicit 0.0 ("Strongly dislike") means NEVER add the item — it is
//     hard-excluded from candidates AND excluded from the average below;
//   • an UNRATED item (no stored value) falls back to the DYNAMIC DEFAULT: the
//     mean of every explicit non-zero rating pooled across recipes + ingredients
//     (so a generous rater's unrated items aren't unfairly disadvantaged);
//   • if nothing is explicitly rated, the neutral fallback 0.5 is used.
//
// This module is pure (no DB / AWS) so it's cheaply unit-testable.

/** The 7-point scale: value in [0,1] + its human label, lowest → highest. */
export const RATING_POINTS = Object.freeze([
  { value: 0, label: 'Strongly dislike (won’t be added to menus)' },
  { value: 1 / 6, label: 'Dislike' },
  { value: 2 / 6, label: 'Slightly dislike' },
  { value: 3 / 6, label: 'Neutral' },
  { value: 4 / 6, label: 'Slightly like' },
  { value: 5 / 6, label: 'Like' },
  { value: 1, label: 'Strongly like' },
]);

/** Neutral fallback when no explicit non-zero ratings exist anywhere. */
export const NEUTRAL_RATING = 0.5;

/** True if `v` is a stored (explicit) rating — a finite number in [0,1]. */
export function hasExplicitRating(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
}

/** Snap an arbitrary [0,1] value to the nearest point on the 7-point scale. */
export function snapToPoint(v) {
  if (!Number.isFinite(v)) return NEUTRAL_RATING;
  const clamped = Math.min(1, Math.max(0, v));
  let best = RATING_POINTS[0];
  let bestDist = Infinity;
  for (const p of RATING_POINTS) {
    const d = Math.abs(p.value - clamped);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best.value;
}

/** Human label for the scale point nearest `v` (e.g. 0.83 → "Like"). */
export function labelForValue(v) {
  const snapped = snapToPoint(v);
  const point = RATING_POINTS.find((p) => p.value === snapped);
  return point ? point.label : 'Neutral';
}

/** The zero-based index of the scale point nearest `v` (for slider position). */
export function indexForValue(v) {
  const snapped = snapToPoint(v);
  return RATING_POINTS.findIndex((p) => p.value === snapped);
}

/**
 * The dynamic default for unrated items: the mean of every explicit NON-ZERO
 * rating across the supplied values, or NEUTRAL_RATING if there are none.
 *
 * @param values iterable of ratings (undefined/null entries are ignored;
 *               explicit 0 is treated as "never" and excluded from the mean).
 */
export function dynamicDefault(values) {
  let sum = 0;
  let n = 0;
  for (const v of values) {
    if (hasExplicitRating(v) && v > 0) {
      sum += v;
      n += 1;
    }
  }
  return n === 0 ? NEUTRAL_RATING : sum / n;
}

/**
 * Resolve every recipe's + ingredient's EFFECTIVE rating for generation.
 *
 * Recipes carry `rating` on the row; ingredient ratings come from the per-user
 * overlay (`prefsByIngredientId`, a Map/obj of ingredientId → rating). The
 * dynamic default is computed once over all explicit non-zero ratings pooled
 * across both types, then applied to any unrated item.
 *
 * @param recipes  array of recipe rows ({ itemId, rating? })
 * @param ingredients array of ingredient rows ({ itemId })
 * @param prefsByIngredientId Map or plain object: ingredientId → rating (0..1)
 * @returns {
 *   default: number,            // the dynamic default applied to unrated items
 *   ratingByRef: Map<string, number>  // ref (recipe/ingredient itemId) → effective rating
 * }
 */
export function resolveRatings(recipes = [], ingredients = [], prefsByIngredientId = new Map()) {
  const prefGet = (id) =>
    prefsByIngredientId instanceof Map ? prefsByIngredientId.get(id) : prefsByIngredientId?.[id];

  // Pool every explicit rating to compute the dynamic default.
  const explicit = [];
  for (const r of recipes) explicit.push(r?.rating);
  for (const ing of ingredients) explicit.push(prefGet(ing?.itemId));
  const def = dynamicDefault(explicit);

  const ratingByRef = new Map();
  for (const r of recipes) {
    if (!r?.itemId) continue;
    ratingByRef.set(r.itemId, hasExplicitRating(r.rating) ? r.rating : def);
  }
  for (const ing of ingredients) {
    if (!ing?.itemId) continue;
    const pref = prefGet(ing.itemId);
    ratingByRef.set(ing.itemId, hasExplicitRating(pref) ? pref : def);
  }
  return { default: def, ratingByRef };
}
